import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, GraduationCap, BookOpen, ChevronRight } from 'lucide-react';

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOptionClick = (type: 'ilkokul' | 'ortaokul') => {
    // For now, open a Google Form or navigate — you can replace these URLs
    const urls: Record<string, string> = {
      ilkokul: 'https://forms.gle/NC6esESKfjgFmE6c6',
      ortaokul: 'https://forms.gle/FbT5YpVczWhcX4FMA',
    };
    window.open(urls[type], '_blank');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>{`
            .app-modal-overlay {
              position: fixed;
              inset: 0;
              z-index: 9999;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 1rem;
            }
            .app-modal-backdrop {
              position: absolute;
              inset: 0;
              background: rgba(15, 45, 94, 0.45);
              backdrop-filter: blur(12px);
              -webkit-backdrop-filter: blur(12px);
            }
            .app-modal-container {
              position: relative;
              z-index: 2;
              width: 100%;
              max-width: 460px;
              background: rgba(255, 255, 255, 0.97);
              border-radius: 24px;
              box-shadow: 
                0 30px 80px rgba(15, 45, 94, 0.25),
                0 0 0 1px rgba(230, 81, 0, 0.1),
                0 0 60px rgba(230, 81, 0, 0.08);
              overflow: hidden;
            }
            .app-modal-header {
              position: relative;
              padding: 28px 28px 20px;
              background: linear-gradient(135deg, #0f2d5e 0%, #1a4a8a 100%);
              overflow: hidden;
            }
            .app-modal-header::before {
              content: '';
              position: absolute;
              top: -50%;
              right: -30%;
              width: 200px;
              height: 200px;
              background: radial-gradient(circle, rgba(230, 81, 0, 0.3) 0%, transparent 70%);
              border-radius: 50%;
              filter: blur(40px);
            }
            .app-modal-header::after {
              content: '';
              position: absolute;
              bottom: -40%;
              left: -20%;
              width: 160px;
              height: 160px;
              background: radial-gradient(circle, rgba(255, 152, 0, 0.2) 0%, transparent 70%);
              border-radius: 50%;
              filter: blur(30px);
            }
            .app-modal-close {
              position: absolute;
              top: 16px;
              right: 16px;
              width: 36px;
              height: 36px;
              border-radius: 50%;
              border: none;
              background: rgba(255, 255, 255, 0.15);
              color: rgba(255, 255, 255, 0.8);
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.3s ease;
              z-index: 10;
            }
            .app-modal-close:hover {
              background: rgba(255, 255, 255, 0.25);
              color: #fff;
              transform: rotate(90deg);
            }
            .app-modal-title {
              font-family: var(--font-headline);
              font-size: 20px;
              font-weight: 900;
              color: #fff;
              letter-spacing: -0.02em;
              margin-bottom: 6px;
              position: relative;
              z-index: 2;
            }
            .app-modal-subtitle {
              font-family: var(--font-body);
              font-size: 13px;
              font-weight: 500;
              color: rgba(255, 255, 255, 0.7);
              line-height: 1.5;
              position: relative;
              z-index: 2;
            }
            .app-modal-body {
              padding: 24px;
              display: flex;
              flex-direction: column;
              gap: 14px;
            }
            .app-modal-option {
              position: relative;
              display: flex;
              align-items: center;
              gap: 16px;
              padding: 20px;
              border-radius: 16px;
              border: 2px solid #f1f5f9;
              background: #fff;
              cursor: pointer;
              transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
              text-decoration: none;
              overflow: hidden;
            }
            .app-modal-option::before {
              content: '';
              position: absolute;
              inset: 0;
              background: linear-gradient(135deg, rgba(230, 81, 0, 0.04) 0%, rgba(255, 152, 0, 0.02) 100%);
              opacity: 0;
              transition: opacity 0.3s ease;
            }
            .app-modal-option:hover {
              border-color: #e65100;
              transform: translateY(-2px);
              box-shadow: 0 12px 30px rgba(230, 81, 0, 0.12), 0 0 0 1px rgba(230, 81, 0, 0.05);
            }
            .app-modal-option:hover::before {
              opacity: 1;
            }
            .app-modal-option:hover .app-modal-icon {
              background: linear-gradient(135deg, #e65100 0%, #ff9800 100%);
              color: #fff;
              transform: scale(1.05);
              box-shadow: 0 8px 20px rgba(230, 81, 0, 0.3);
            }
            .app-modal-option:hover .app-modal-arrow {
              opacity: 1;
              transform: translateX(0);
              color: #e65100;
            }
            .app-modal-option:active {
              transform: translateY(0px) scale(0.98);
            }
            .app-modal-icon {
              width: 52px;
              height: 52px;
              min-width: 52px;
              border-radius: 14px;
              background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: #0f2d5e;
              transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
              position: relative;
              z-index: 2;
            }
            .app-modal-option-content {
              flex: 1;
              position: relative;
              z-index: 2;
            }
            .app-modal-option-title {
              font-family: var(--font-headline);
              font-size: 15px;
              font-weight: 800;
              color: #0f2d5e;
              letter-spacing: 0.02em;
              margin-bottom: 3px;
            }
            .app-modal-option-desc {
              font-family: var(--font-body);
              font-size: 12px;
              font-weight: 500;
              color: #64748b;
              line-height: 1.4;
            }
            .app-modal-arrow {
              opacity: 0;
              transform: translateX(-8px);
              transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
              color: #94a3b8;
              position: relative;
              z-index: 2;
            }
            .app-modal-footer {
              padding: 0 24px 20px;
              text-align: center;
            }
            .app-modal-footer-text {
              font-family: var(--font-body);
              font-size: 11px;
              color: #94a3b8;
              font-weight: 500;
            }
          `}</style>

          <motion.div
            className="app-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Backdrop */}
            <motion.div
              className="app-modal-backdrop"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal */}
            <motion.div
              className="app-modal-container"
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            >
              {/* Header */}
              <div className="app-modal-header">
                <button className="app-modal-close" onClick={onClose}>
                  <X size={18} />
                </button>
                <div className="app-modal-title">Başvuru Türünü Seçin</div>
                <div className="app-modal-subtitle">
                  Drone kurs programlarımızdan size uygun olanı seçerek başvurunuzu yapın.
                </div>
              </div>

              {/* Body */}
              <div className="app-modal-body">
                {/* İlkokul */}
                <motion.div
                  className="app-modal-option"
                  onClick={() => handleOptionClick('ilkokul')}
                  whileTap={{ scale: 0.97 }}
                >
                  <div className="app-modal-icon">
                    <BookOpen size={24} strokeWidth={2} />
                  </div>
                  <div className="app-modal-option-content">
                    <div className="app-modal-option-title">İlkokul Kurs Başvuru</div>
                    <div className="app-modal-option-desc">İlkokul öğrencileri için drone eğitim programı</div>
                  </div>
                  <ChevronRight size={20} className="app-modal-arrow" />
                </motion.div>

                {/* Ortaokul */}
                <motion.div
                  className="app-modal-option"
                  onClick={() => handleOptionClick('ortaokul')}
                  whileTap={{ scale: 0.97 }}
                >
                  <div className="app-modal-icon">
                    <GraduationCap size={24} strokeWidth={2} />
                  </div>
                  <div className="app-modal-option-content">
                    <div className="app-modal-option-title">Ortaokul Kurs Başvuru</div>
                    <div className="app-modal-option-desc">Ortaokul öğrencileri için ileri seviye drone eğitimi</div>
                  </div>
                  <ChevronRight size={20} className="app-modal-arrow" />
                </motion.div>
              </div>


            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ApplicationModal;
